import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Table,
  Box,
  Badge,
  StatusIndicator,
  Button,
  ColumnLayout,
  Pagination,
  DateRangePicker,
  PropertyFilter,
} from '@cloudscape-design/components';
import { getAuditLog, getAuditCounts } from '../services/s3Service';
import { buildAuditKey, getAuditSnapshot, saveAuditSnapshot } from '../services/auditCache';
import { reconcilePage0 } from '../services/auditPaging';
import { Link } from 'react-router-dom';

const PAGE_SIZE = 25;

const OPERATION_COLORS = {
  INSERT: 'green',
  MODIFY: 'blue',
  LOGIN: 'grey',
  VISIT: 'grey',
  VIEW: 'blue',
  REVIEW_CALCULATOR: 'blue',
};

const getOperationLabel = (operation, tableName, newImage) => {
  if (operation === 'LOGIN') return 'Login';
  if (operation === 'VISIT') return 'Visit';
  if (operation === 'VIEW') return 'View';

  if (tableName === 'chronos_calculators') return 'Calculator Review';

  if (tableName === 'chronos_reviewer_feedback') {
    if (operation === 'INSERT') return 'Feedback';
    if (operation === 'MODIFY') return 'Reply';
  }

  if (tableName === 'chronos_validated_fundrequests') {
    if (operation === 'INSERT') return 'Validated';
    if (operation === 'MODIFY') return 'Re-validated';
    if (operation === 'REMOVE') return 'Removed';
  }

  if (tableName === 'chronos_review_state') {
    if (operation === 'INSERT') return 'Review';
    if (operation === 'MODIFY') {
      const newState = newImage?.review_state?.S;
      if (newState === '4-Failed') return 'Failed';
      return 'Revalidation';
    }
  }

  return operation;
};

const getOperationColor = (operation, tableName, newImage) => {
  const label = getOperationLabel(operation, tableName, newImage);
  if (label === 'Failed') return 'red';
  return OPERATION_COLORS[operation] || 'grey';
};

const extractProgram = (recordKey) => {
  if (!recordKey) return '—';
  const match = recordKey.match(/FR-([A-Z]+)-/);
  return match ? match[1] : '—';
};

const formatDate = (isoString) => {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const extractUser = (item) => {
  // Check top-level user field first (set by all new audit records)
  let raw = '';
  if (item.user) raw = item.user;
  else if (item.operation === 'LOGIN' && item.new_image) {
    const img = item.new_image;
    raw = img.email?.S || img.email || '';
  } else if (item.new_image) {
    const img = item.new_image;
    raw = img.triggered_by?.S || img.triggered_by
      || img.reviewer_alias?.S || img.reviewer_alias
      || img.reviewer_id?.S || img.reviewer_id || '';
  }
  if (!raw) return 'System';
  // Strip domain to get alias
  return raw.includes('@') ? raw.split('@')[0] : raw;
};

// Unmarshall DynamoDB typed attributes ({S: "val"}, {N: "1"}, {BOOL: true}) to plain values
const unmarshallValue = (v) => {
  if (v === null || v === undefined) return v;
  if (typeof v !== 'object') return v;
  if (v.S !== undefined) return v.S;
  if (v.N !== undefined) return Number(v.N);
  if (v.BOOL !== undefined) return v.BOOL;
  if (v.NULL) return null;
  if (v.L) return v.L.map(unmarshallValue);
  if (v.M) return unmarshallImage(v.M);
  // Already plain object or array
  if (Array.isArray(v)) return v.map(unmarshallValue);
  return unmarshallImage(v);
};

const unmarshallImage = (image) => {
  if (!image || typeof image !== 'object') return image;
  const result = {};
  for (const [key, val] of Object.entries(image)) {
    result[key] = unmarshallValue(val);
  }
  return result;
};

// Format record_key for display: "FR_id=FR-MAP-CASH-TVBM-xxx#reviewer_id=user" → linkable FR_id + readable parts
const formatRecordKey = (recordKey) => {
  if (!recordKey) return '—';
  const parts = recordKey.split('#');
  return parts.map((part, i) => {
    const eqIdx = part.indexOf('=');
    if (eqIdx === -1) return <span key={i}>{part}</span>;
    const value = part.substring(eqIdx + 1);
    // Make FR_id / apfp_fundrequestid values clickable links (both are the
    // same FR-... id space the detail page resolves; see list_validations
    // which exposes apfp_fundrequestid as the nav `id`).
    if ((part.startsWith('FR_id=') || part.startsWith('apfp_fundrequestid=')) && value.startsWith('FR-')) {
      return (
        <span key={i}>
          {i > 0 && <br />}
          <Link to={`/fund-requests/${encodeURIComponent(value)}/details`}>{value}</Link>
        </span>
      );
    }
    return <span key={i}>{i > 0 && <br />}{value}</span>;
  });
};

const MAX_CHANGE_LINES = 8;

const formatChangedFields = (item) => {
  const fields = item.changed_fields;
  if (!fields || fields.length === 0) return '—';
  if (item.operation !== 'MODIFY' || !item.old_image || !item.new_image) {
    // INSERT/REMOVE or missing images: just the changed field names, capped.
    if (fields.length > MAX_CHANGE_LINES) {
      return `${fields.slice(0, MAX_CHANGE_LINES).join(', ')}, … +${fields.length - MAX_CHANGE_LINES} more`;
    }
    return fields.join(', ');
  }
  const fmt = (v) => {
    if (v === undefined || v === null) return '∅';
    let s;
    if (typeof v === 'object' && (v.S !== undefined || v.N !== undefined || v.BOOL !== undefined)) {
      s = v.S !== undefined ? v.S : (v.N !== undefined ? String(v.N) : String(v.BOOL));
    } else {
      s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    }
    return s.length > 40 ? s.substring(0, 40) + '…' : s;
  };
  const lines = fields.slice(0, MAX_CHANGE_LINES).map((field) => {
    return `${field}: ${fmt(item.old_image[field])} → ${fmt(item.new_image[field])}`;
  });
  if (fields.length > MAX_CHANGE_LINES) {
    lines.push(`… +${fields.length - MAX_CHANGE_LINES} more field(s)`);
  }
  return lines.join('\n');
};

// Default date range — computed ONCE per SPA session (module scope), not per
// mount, so the cache key stays stable across a midnight rollover and re-entry
// stays instant (REVIEW-FIX #6). Open-ended at the top: no endDate, so the view
// always includes the latest events even after the day changes; the backend
// treats a missing end_date as "up to now". The start is a 30-day lookback.
const DEFAULT_DATE_RANGE = (() => {
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return {
    type: 'absolute',
    startDate: start.toISOString().split('T')[0],
    endDate: '', // open-ended top (no upper bound)
  };
})();

const exportCsv = (items) => {
  const headers = ['Timestamp', 'Program', 'Operation', 'User', 'Record Key', 'Changed Fields'];
  const rows = items.map((item) => [
    item.timestamp || '',
    extractProgram(item.record_key),
    getOperationLabel(item.operation, item.table_name, item.new_image),
    extractUser(item),
    item.record_key || '',
    (item.changed_fields || []).join('; '),
  ]);
  const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const FILTERING_PROPERTIES = [
  { key: 'program', propertyLabel: 'Program', groupValuesLabel: 'Program values', operators: ['='] },
  { key: 'operation', propertyLabel: 'Operation', groupValuesLabel: 'Operation values', operators: ['='] },
  { key: 'user', propertyLabel: 'User', groupValuesLabel: 'User values', operators: ['=', ':'] },
  { key: 'record_key', propertyLabel: 'Record Key', groupValuesLabel: 'Record Key values', operators: ['=', ':'] },
];

// Map PropertyFilter operation display values back to DynamoDB values
const OPERATION_REVERSE = {
  'review': 'INSERT',
  'revalidation': 'MODIFY',
  'login': 'LOGIN',
  'visit': 'VISIT',
  'view': 'VIEW',
  'calculator review': 'REVIEW_CALCULATOR',
};

// Extract server-side filterable tokens — all filters go to the API
const extractServerFilters = (query) => {
  const filters = {};
  if (!query.tokens) return filters;
  for (const token of query.tokens) {
    if (token.propertyKey === 'program' && token.operator === '=') {
      filters.recordKey = `FR-${token.value.toUpperCase()}-`;
    } else if (token.propertyKey === 'operation' && token.operator === '=') {
      filters.operation = OPERATION_REVERSE[token.value.toLowerCase()] || token.value.toUpperCase();
    } else if (token.propertyKey === 'user') {
      filters.user = token.value;
    } else if (token.propertyKey === 'record_key') {
      filters.recordKey = token.value;
    }
  }
  return filters;
};

const ALL_TABLES = ['chronos_review_state', 'chronos_reviewer_feedback', 'cognito_auth', 'chronos_calculators', 'chronos_validated_fundrequests'];

const AuditReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(DEFAULT_DATE_RANGE);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [sortingColumn, setSortingColumn] = useState({ sortingField: 'timestamp' });
  const [sortingDescending, setSortingDescending] = useState(true);
  const [filterQuery, setFilterQuery] = useState({ tokens: [], operation: 'and' });
  const [counts, setCounts] = useState(null);
  const [exporting, setExporting] = useState(false);

  // [A] page mode (server-side pagination) state
  const [pageItems, setPageItems] = useState([]);        // current 25-row page
  const [pageIndex, setPageIndex] = useState(0);          // 0-based current page
  const tokenStack = useRef([null]);                      // tokenStack[i] = token that loads page i
  const pageCache = useRef(new Map());                    // pageIndex -> { items, nextToken } (visited + prefetched)
  const maxReachedRef = useRef(0);                        // furthest page index reached (for stable page numbers)
  const [frontier, setFrontier] = useState({ max: 0, more: false }); // pagination UI: keep numbers up to furthest reached

  const getDateFilters = useCallback(() => {
    const filters = {};
    if (dateRange && dateRange.type === 'absolute') {
      if (dateRange.startDate) filters.startDate = dateRange.startDate;
      if (dateRange.endDate) filters.endDate = dateRange.endDate;
    }
    return filters;
  }, [dateRange]);

  const serverFilters = useMemo(() => extractServerFilters(filterQuery), [filterQuery]);

  // Only Timestamp is sortable (server-side, both directions via sort_dir);
  // the other columns are filters. sortDirRef is read by fetchPage (avoids a stale closure).
  const sortDirRef = useRef('desc');

  // counts: loaded only on filter/date change, in parallel; never on page nav.
  const loadCounts = useCallback(async (srvFilters) => {
    try {
      const data = await getAuditCounts({ ...getDateFilters(), ...srvFilters });
      setCounts(data);
    } catch (err) {
      console.error('Failed to load audit counts:', err); // must not block the table
    }
  }, [getDateFilters]);

  // [A] fetch a single server page (one merged page across all partitions).
  const fetchPage = useCallback(async (token, srvFilters) => {
    const params = { ...getDateFilters(), ...srvFilters, pageSize: PAGE_SIZE, sortDir: sortDirRef.current };
    if (token) params.nextToken = token;
    return getAuditLog(params);
  }, [getDateFilters]);

  // Track the furthest page reached so page numbers don't shrink when paging
  // back. Only grows; going back to an earlier (cached) page keeps the numbers.
  const recordReach = useCallback((idx, nt) => {
    if (idx >= maxReachedRef.current) {
      maxReachedRef.current = idx;
      setFrontier({ max: idx, more: nt !== null });
    }
  }, []);

  // Show a freshly-loaded page: update UI, cache it by index, record the token
  // for the next page (sync, so a fast goNext on a cache miss has the token),
  // and lazily prefetch the next page (±1 window, non-blocking).
  const showPage = useCallback((idx, token, data, srvFilters) => {
    const items = data.auditRecords || [];
    const nt = data.nextToken || null;
    pageCache.current.set(idx, { items, nextToken: nt });
    tokenStack.current[idx] = token;
    if (nt) tokenStack.current[idx + 1] = nt;
    setPageItems(items);
    setPageIndex(idx);
    recordReach(idx, nt);
    if (nt && !pageCache.current.has(idx + 1)) {
      fetchPage(nt, srvFilters)
        .then((d) => pageCache.current.set(idx + 1, { items: d.auditRecords || [], nextToken: d.nextToken || null }))
        .catch(() => {});
    }
  }, [fetchPage, recordReach]);

  // [B] exhaustive load (only for non-default sorts) — tables fetched in PARALLEL.
  const ensureAllLoaded = useCallback(async (srvFilters) => {
    const baseFilters = { ...getDateFilters(), ...srvFilters };
    const perTable = await Promise.all(
      ALL_TABLES.map(async (tableName) => {
        let token = null;
        let rows = [];
        do {
          const params = { ...baseFilters, tableName, pageSize: 100 };
          if (token) params.nextToken = token;
          const data = await getAuditLog(params);
          rows = rows.concat(data.auditRecords || []);
          token = data.nextToken || null;
        } while (token);
        return rows;
      })
    );
    const merged = perTable.flat();
    merged.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
    return merged;
  }, [getDateFilters]);

  // ── Stale-while-revalidate across navigation (see auditCache) ─────────────

  // View key: filters + date window + sort.
  const viewKey = useMemo(
    () => buildAuditKey(serverFilters, dateRange, sortingColumn.sortingField, sortingDescending),
    [serverFilters, dateRange, sortingColumn, sortingDescending]
  );

  // pageIndex in a ref so background revalidation reads the live page.
  const pageIndexRef = useRef(0);
  useEffect(() => { pageIndexRef.current = pageIndex; }, [pageIndex]);

  // Snapshot-able state, kept current every render for the unmount handler.
  const snapStateRef = useRef({});
  const keyRef = useRef(viewKey);
  keyRef.current = viewKey;
  snapStateRef.current = {
    pageItems, pageIndex, frontier, counts,
    pageCache: pageCache.current, tokenStack: tokenStack.current,
    maxReached: maxReachedRef.current, sortDir: sortDirRef.current,
  };

  // Restore a snapshot instantly (no spinner): repopulate refs + state.
  const restoreFromSnapshot = useCallback((snap) => {
    const s = snap.state || {};
    // Copy on restore so later ref mutations (clear/set/delete, tokenStack[idx]=)
    // can't corrupt the cached snapshot — e.g. a StrictMode double-invoke or a
    // rapid remount would otherwise read emptied/stale data.
    pageCache.current = s.pageCache instanceof Map ? new Map(s.pageCache) : new Map();
    tokenStack.current = Array.isArray(s.tokenStack) ? [...s.tokenStack] : [null];
    maxReachedRef.current = s.maxReached || 0;
    sortDirRef.current = s.sortDir || 'desc';
    setPageItems(s.pageItems || []);
    setPageIndex(s.pageIndex || 0);
    pageIndexRef.current = s.pageIndex || 0; // sync the ref now; the [pageIndex] effect runs later
    setFrontier(s.frontier || { max: 0, more: false });
    setCounts(s.counts || null);
    setLoading(false);
    setError(null);
  }, []);

  // Background delta refresh after a snapshot restore. Never shows a spinner or
  // disrupts the restored view; failures are swallowed.
  const revalidateDelta = useCallback(async (srvFilters) => {
    loadCounts(srvFilters); // counts refresh quietly
    try {
      {
        // Reconcile only when on page 0 — deeper pages hold their position until
        // an explicit Refresh (REVIEW-FIX #3: page 0/1 stay in sync). The
        // descending/ascending behavior is handled inside reconcilePage0.
        if (pageIndexRef.current === 0) {
          const prev0 = pageCache.current.get(0);
          const data = await fetchPage(null, srvFilters);
          const items = data.auditRecords || [];
          const nt = data.nextToken || null;

          const { changed, tokenStack: resetStack, frontier: resetFrontier } =
            reconcilePage0(prev0 && prev0.items, items, nt, pageCache.current);
          tokenStack.current[0] = null;
          if (nt) tokenStack.current[1] = nt;

          if (changed) {
            // Boundary moved: apply the reset cursors/frontier from reconcilePage0
            // and re-prefetch the new page 1 so forward navigation stays instant.
            tokenStack.current = resetStack;
            maxReachedRef.current = 0;
            setFrontier(resetFrontier);
            if (nt) {
              fetchPage(nt, srvFilters)
                .then((d) => pageCache.current.set(1, { items: d.auditRecords || [], nextToken: d.nextToken || null }))
                .catch(() => {});
            }
          }
          setPageItems(items);
        }
      }
    } catch (e) {
      console.debug('Audit delta revalidation failed:', e); // non-disruptive
    }
  }, [loadCounts, fetchPage]);

  // Save the snapshot on unmount (route change).
  useEffect(() => {
    return () => {
      const s = snapStateRef.current;
      // Skip empty snapshots (StrictMode's dev mount→unmount cycle fires this
      // before the first load lands). `counts` is set once any load completes.
      const hasData = !!s.counts
        || (s.pageItems && s.pageItems.length > 0);
      if (!hasData) return;
      // Copy the mutable refs (Map/Array) so post-save ref mutations in the
      // live component don't reach into and corrupt the stored snapshot.
      saveAuditSnapshot(keyRef.current, {
        ...s,
        pageCache: new Map(s.pageCache),
        tokenStack: [...s.tokenStack],
      });
    };
  }, []);

  // Reload on filter / date change. Sort changes are handled imperatively in
  // onSortingChange. On entry, prefer a cached snapshot for this exact view
  // (render instantly, revalidate the delta); full load only when none exists.
  // Restoring is idempotent, so StrictMode's double-invoke in dev is safe.
  useEffect(() => {
    const snap = getAuditSnapshot(viewKey);
    if (snap) {
      restoreFromSnapshot(snap);
      revalidateDelta(serverFilters);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setExpandedItems(new Set());
      pageCache.current.clear();
      tokenStack.current = [null];
      maxReachedRef.current = 0;
      setFrontier({ max: 0, more: false });
      loadCounts(serverFilters);
      try {
        const data = await fetchPage(null, serverFilters);
        if (!cancelled) showPage(0, null, data, serverFilters);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load audit data:', err);
          setError(err.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [serverFilters, dateRange, fetchPage, showPage, loadCounts]); // eslint-disable-line react-hooks/exhaustive-deps

  // [A] navigate to a page index. Cache hit (any visited or prefetched page,
  // including page 0 and any "previous" page) → instant, no network, no spinner.
  // Cache miss (a genuinely new forward page) → fetch using the recorded token.
  const goToIndex = useCallback(async (idx) => {
    const hit = pageCache.current.get(idx);
    if (hit) {
      setPageItems(hit.items);
      setPageIndex(idx);
      recordReach(idx, hit.nextToken);
      if (hit.nextToken && !pageCache.current.has(idx + 1)) {
        tokenStack.current[idx + 1] = hit.nextToken;
        fetchPage(hit.nextToken, serverFilters)
          .then((d) => pageCache.current.set(idx + 1, { items: d.auditRecords || [], nextToken: d.nextToken || null }))
          .catch(() => {});
      }
      return;
    }
    const token = tokenStack.current[idx] ?? null;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPage(token, serverFilters);
      showPage(idx, token, data, serverFilters);
    } catch (err) {
      console.error('Failed to load page:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchPage, showPage, recordReach, serverFilters]);

  const displayItems = pageItems;

  // Open-ended cursor pagination (prev/next + jump to any already-reached page).
  const paginationProps = {
    openEnd: frontier.more,
    currentPageIndex: pageIndex + 1,
    pagesCount: Math.max(frontier.max + 1, 1),
    onChange: ({ detail }) => {
      const idx = detail.currentPageIndex - 1;
      if (idx < 0) return;
      if (idx <= frontier.max) goToIndex(idx);             // visited/loaded: instant jump
      else if (frontier.more) goToIndex(frontier.max + 1); // extend frontier by one
    },
  };

  const c = counts || {};
  const byTblOp = c.byTableOperation || {};

  const toggleExpand = (auditId) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(auditId)) next.delete(auditId);
      else next.add(auditId);
      return next;
    });
  };

  const refreshAll = () => {
    pageCache.current.clear();
    tokenStack.current = [null];
    maxReachedRef.current = 0;
    setFrontier({ max: 0, more: false });
    setExpandedItems(new Set());
    loadCounts(serverFilters);
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchPage(null, serverFilters);
        showPage(0, null, data, serverFilters);
      } catch (err) { setError(err.message); }
      finally { setLoading(false); }
    })();
  };

  // CSV export: [B] already holds the full set; [A] fetches it on demand
  // (heavy work moved to explicit user action — server-side export is Part B).
  const handleExportAll = async () => {
    setExporting(true);
    try {
      const all = await ensureAllLoaded(serverFilters);
      // Match the on-screen order (the server returns newest-first; the table
      // may be showing oldest-first), so the CSV row order matches the view.
      const ordered = [...all].sort((a, b) => {
        const cmp = String(a.timestamp || '').localeCompare(String(b.timestamp || ''));
        return sortingDescending ? -cmp : cmp;
      });
      exportCsv(ordered);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  if (error) {
    return (
      <Container header={<Header variant="h1">Event History</Header>}>
        <Box textAlign="center" padding="xxl">
          <StatusIndicator type="error">{error}</StatusIndicator>
          <Box padding={{ top: 'm' }}>
            <Button onClick={refreshAll}>Retry</Button>
          </Box>
        </Box>
      </Container>
    );
  }

  const columnDefinitions = [
    { id: 'timestamp', header: 'Timestamp', cell: (item) => formatDate(item.timestamp), sortingField: 'timestamp', width: 200 },
    { id: 'program', header: 'Program', cell: (item) => { const p = extractProgram(item.record_key); return p === '—' ? '—' : <Badge color="blue">{p}</Badge>; } },
    { id: 'operation', header: 'Operation', cell: (item) => <Badge color={getOperationColor(item.operation, item.table_name, item.new_image)}>{getOperationLabel(item.operation, item.table_name, item.new_image)}</Badge> },
    { id: 'user', header: 'User', cell: (item) => { const alias = extractUser(item); return alias === 'System' ? alias : <a href={`https://phonetool.amazon.com/users/${alias}`} target="_blank" rel="noopener noreferrer">{alias}</a>; } },
    { id: 'record_key', header: 'Record Key', cell: (item) => formatRecordKey(item.record_key), width: 280 },
    {
      id: 'changed_fields', header: 'Changes',
      cell: (item) => {
        const text = formatChangedFields(item);
        if (text === '—') return '—';
        return <Box variant="code"><pre style={{ fontSize: '11px', margin: 0, whiteSpace: 'pre-wrap' }}>{text}</pre></Box>;
      },
    },
    {
      id: 'details', header: '', width: 120,
      cell: (item) => {
        const hasData = item.old_image || item.new_image;
        if (!hasData) return null;
        const isExpanded = expandedItems.has(item.audit_id);
        return <Button variant="inline-link" onClick={() => toggleExpand(item.audit_id)}>{isExpanded ? 'Hide' : 'View'} details</Button>;
      },
    },
  ];

  return (
    <SpaceBetween size="l">
      <Header variant="h1">Event History</Header>

      <Container>
        <ColumnLayout columns={5} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Total Events</Box>
            <Box variant="awsui-value-large">{counts ? c.total : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Users</Box>
            <Box variant="awsui-value-large">{counts ? (c.unique_users || 0) : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Reviews</Box>
            <Box variant="awsui-value-large" color="text-status-success">{counts ? (byTblOp['chronos_review_state#INSERT'] || 0) : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Revalidations</Box>
            <Box variant="awsui-value-large">{counts ? (byTblOp['chronos_review_state#MODIFY'] || 0) : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Failed</Box>
            <Box variant="awsui-value-large" color="text-status-error">{counts ? (byTblOp['chronos_review_state#FAILED'] || 0) : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Feedback</Box>
            <Box variant="awsui-value-large">{counts ? (byTblOp['chronos_reviewer_feedback#INSERT'] || 0) : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Replies</Box>
            <Box variant="awsui-value-large">{counts ? (byTblOp['chronos_reviewer_feedback#MODIFY'] || 0) : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Logins</Box>
            <Box variant="awsui-value-large">{counts ? (byTblOp['cognito_auth#LOGIN'] || 0) : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Visits</Box>
            <Box variant="awsui-value-large">{counts ? (byTblOp['cognito_auth#VISIT'] || 0) : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Views</Box>
            <Box variant="awsui-value-large" color="text-status-info">{counts ? (byTblOp['chronos_review_state#VIEW'] || 0) : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Calculator Reviews</Box>
            <Box variant="awsui-value-large">{counts ? (byTblOp['chronos_calculators#REVIEW_CALCULATOR'] || 0) : '—'}</Box>
          </div>
        </ColumnLayout>
      </Container>

      <Table
        header={
          <Header variant="h2" counter={`(${counts ? c.total : displayItems.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <DateRangePicker
                  value={dateRange}
                  onChange={({ detail }) => {
                    const v = detail.value;
                    if (v && v.type === 'absolute') {
                      setDateRange({
                        ...v,
                        startDate: v.startDate ? v.startDate.split('T')[0] : v.startDate,
                        endDate: v.endDate ? v.endDate.split('T')[0] : v.endDate,
                      });
                    } else {
                      setDateRange(v);
                    }
                  }}
                  relativeOptions={[]}
                  rangeSelectorMode="absolute-only"
                  dateOnly={true}
                  placeholder="Time range"
                  i18nStrings={{
                    absoluteModeTitle: 'Absolute',
                    cancelButtonLabel: 'Cancel', applyButtonLabel: 'Apply',
                    clearButtonLabel: 'Clear', startDateLabel: 'Start date',
                    endDateLabel: 'End date',
                  }}
                  isValidRange={(range) => {
                    // Open-ended top is allowed: only a start date is required;
                    // a missing end means "up to now" (REVIEW-FIX #6).
                    if (range.type === 'absolute' && !range.startDate) {
                      return { valid: false, errorMessage: 'Select a start date' };
                    }
                    return { valid: true };
                  }}
                />
                <Button iconName="refresh" onClick={refreshAll} loading={loading}>Refresh</Button>
                <Button iconName="download" onClick={handleExportAll} loading={exporting}>Export CSV</Button>
              </SpaceBetween>
            }
          >
            Audit Events
          </Header>
        }
        loading={loading}
        loadingText="Loading audit log..."
        resizableColumns
        wrapLines
        items={displayItems}
        empty={
          <Box textAlign="center" padding="xxl">
            <Box variant="h3" color="text-status-inactive">No audit events found</Box>
            <Box variant="p" color="text-body-secondary" padding={{ top: 's' }}>
              Audit events are captured automatically when data changes occur.
            </Box>
          </Box>
        }
        columnDefinitions={columnDefinitions}
        sortingColumn={sortingColumn}
        sortingDescending={sortingDescending}
        onSortingChange={({ detail }) => {
          // Only Timestamp is sortable: reset the page cache and reload page 0
          // with the new sort_dir (cached pages are for the other direction).
          setSortingColumn(detail.sortingColumn);
          setSortingDescending(detail.isDescending);
          sortDirRef.current = detail.isDescending ? 'desc' : 'asc';
          pageCache.current.clear();
          tokenStack.current = [null];
          maxReachedRef.current = 0;
          setFrontier({ max: 0, more: false });
          (async () => {
            setLoading(true);
            setError(null);
            try { const data = await fetchPage(null, serverFilters); showPage(0, null, data, serverFilters); }
            catch (err) { setError(err.message); }
            finally { setLoading(false); }
          })();
        }}
        filter={
          <PropertyFilter
            query={filterQuery}
            onChange={({ detail }) => { setFilterQuery(detail); }}
            filteringProperties={FILTERING_PROPERTIES}
            filteringOptions={[
              { propertyKey: 'program', value: 'MAP' },
              { propertyKey: 'operation', value: 'Review' },
              { propertyKey: 'operation', value: 'Revalidation' },
              { propertyKey: 'operation', value: 'Login' },
              { propertyKey: 'operation', value: 'Visit' },
              { propertyKey: 'operation', value: 'View' },
              { propertyKey: 'operation', value: 'Calculator Review' },
            ]}
            filteringPlaceholder="Filter audit events by program, operation, user, or record key"
            i18nStrings={{
              filteringAriaLabel: 'Filter audit events', dismissAriaLabel: 'Dismiss',
              filteringPlaceholder: 'Filter audit events',
              groupValuesText: 'Values', groupPropertiesText: 'Properties',
              operatorsText: 'Operators', operationAndText: 'and', operationOrText: 'or',
              operatorLessText: 'Less than', operatorLessOrEqualText: 'Less than or equal',
              operatorGreaterText: 'Greater than', operatorGreaterOrEqualText: 'Greater than or equal',
              operatorContainsText: 'Contains', operatorDoesNotContainText: 'Does not contain',
              operatorEqualsText: 'Equals', operatorDoesNotEqualText: 'Does not equal',
              editTokenHeader: 'Edit filter', propertyText: 'Property',
              operatorText: 'Operator', valueText: 'Value',
              cancelActionText: 'Cancel', applyActionText: 'Apply',
              allPropertiesLabel: 'All properties',
              tokenLimitShowMore: 'Show more', tokenLimitShowFewer: 'Show fewer',
              clearFiltersText: 'Clear filters',
              removeTokenButtonAriaLabel: (token) => `Remove filter, ${token.propertyKey} ${token.operator} ${token.value}`,
              enteredTextLabel: (text) => `Use: "${text}"`,
            }}
          />
        }
        pagination={
          <Pagination {...paginationProps} />
        }
      />

      {displayItems.map((item) =>
        expandedItems.has(item.audit_id) ? (
          <Container key={item.audit_id} header={<Header variant="h3">Details: {item.record_key}</Header>}>
            <ColumnLayout columns={2}>
              {item.old_image && (
                <Box>
                  <Box variant="awsui-key-label">Old Image</Box>
                  <Box variant="code"><pre style={{ fontSize: '12px', maxHeight: '300px', overflow: 'auto' }}>{JSON.stringify(unmarshallImage(item.old_image), null, 2)}</pre></Box>
                </Box>
              )}
              {item.new_image && (
                <Box>
                  <Box variant="awsui-key-label">New Image</Box>
                  <Box variant="code"><pre style={{ fontSize: '12px', maxHeight: '300px', overflow: 'auto' }}>{JSON.stringify(unmarshallImage(item.new_image), null, 2)}</pre></Box>
                </Box>
              )}
            </ColumnLayout>
          </Container>
        ) : null
      )}
    </SpaceBetween>
  );
};

export default AuditReport;
